/**
 * Bestelstatus volgen (sections/jc-bestelstatus.liquid, LICHT-BESLUITEN L33).
 *
 * Sends order number and e-mail address as a JSON body (never in the URL) to
 * the app proxy and shows what Cortex returns: status, shipments, the items.
 * Everything Cortex returns is written with textContent; a link is only made
 * from an https URL.
 *
 * Tracking: the carrier's page shows the delivery address, and its link holds
 * the postcode as the second key. Cortex returns the link without it
 * (trackinglink_postcode); the customer types the postcode here and the browser
 * opens the carrier's page. The postcode is never sent to Cortex.
 *
 * Answers of POST /apps/cortex/public/bestelstatus:
 *   200 {gevonden: true, bestelling}   404 niet_gevonden   400 ongeldig
 *   429 te_veel_pogingen               403/503 or no answer: unavailable
 */
(() => {
  const form = document.querySelector('[data-jc-order-status]');
  const stringsNode = document.querySelector('[data-jc-order-status-strings]');
  if (!form || !stringsNode || form.dataset.ready) return;
  form.dataset.ready = 'true';

  let t;
  try {
    t = JSON.parse(stringsNode.textContent);
  } catch (error) {
    return;
  }

  const result = document.querySelector('[data-jc-order-status-result]');
  const message = form.querySelector('[data-jc-order-status-message]');
  const submit = form.querySelector('button[type="submit"]');
  const numberInput = form.elements.bestelnummer;
  const emailInput = form.elements.email;
  const locale = form.dataset.locale || document.documentElement.lang || 'nl';
  const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const ORDER_NUMBER = /^#?\s*[A-Za-z]{0,12}[\s-]?\d{1,12}[A-Za-z]{0,6}$/;

  const fill = (text, values) =>
    Object.entries(values).reduce((out, [key, value]) => out.replace(`[${key}]`, value), text);

  const dateText = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-').map(Number);
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        new Date(Date.UTC(year, month - 1, day)),
      );
    } catch (error) {
      return iso;
    }
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const httpsUrl = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' ? url.href : null;
    } catch (error) {
      return null;
    }
  };

  const externalLink = (href, label, className) => {
    const link = el('a', className, label);
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.referrerPolicy = 'no-referrer';
    link.append(el('span', 'visually-hidden', ` ${t.newWindow}`));
    return link;
  };

  const fieldError = (input, text) => {
    const error = document.getElementById(`${input.id}-error`);
    input.setAttribute('aria-invalid', text ? 'true' : 'false');
    if (!error) return;
    error.textContent = text || '';
    error.hidden = !text;
  };

  const showMessage = (text) => {
    message.textContent = text || '';
    message.hidden = !text;
  };

  const setBusy = (busy) => {
    submit.disabled = busy;
    submit.classList.toggle('is-loading', busy);
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  // The carrier takes the postcode without spaces, in either case (measured on
  // goparcel: "1234 AB" gives 404, "1234AB" and "1234ab" work).
  const POSTCODE = /^[A-Z0-9-]{3,10}$/;
  let postcodeForms = 0;

  const openExternal = (href) => {
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.referrerPolicy = 'no-referrer';
    document.body.append(link);
    link.click();
    link.remove();
  };

  const postcodeForm = (base) => {
    postcodeForms += 1;
    const id = `${form.id}-postcode-${postcodeForms}`;
    const box = el('form', 'jc-order-status__postcode');
    box.noValidate = true;
    const label = el('label', 'jc-order-status__label', t.postcode);
    label.htmlFor = id;
    const row = el('div', 'jc-order-status__postcode-row');
    const input = el('input', 'input jc-order-status__input');
    Object.assign(input, { id, name: 'postcode', type: 'text', autocomplete: 'postal-code', maxLength: 10, spellcheck: false });
    input.setAttribute('autocapitalize', 'characters');
    input.setAttribute('aria-describedby', `${id}-hint ${id}-error`);
    const button = el('button', 'btn btn--primary jc-order-status__track', t.track);
    button.type = 'submit';
    button.append(el('span', 'visually-hidden', ` ${t.newWindow}`));
    row.append(input, button);
    const hint = el('p', 'jc-order-status__hint', t.postcodeHint);
    hint.id = `${id}-hint`;
    const error = el('p', 'jc-order-status__field-error');
    error.id = `${id}-error`;
    error.hidden = true;
    box.append(label, row, hint, error);

    box.addEventListener('submit', (event) => {
      event.preventDefault();
      const postcode = input.value.replace(/\s+/g, '').toUpperCase();
      if (!POSTCODE.test(postcode)) {
        fieldError(input, t.postcodeRequired);
        input.focus();
        return;
      }
      fieldError(input, '');
      openExternal(`${base}${encodeURIComponent(postcode)}`);
    });
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') === 'true') fieldError(input, '');
    });
    return box;
  };

  const items = (list) => {
    const ul = el('ul', 'jc-order-status__items');
    list.forEach((item) => {
      const li = el('li', 'jc-order-status__item');
      li.append(el('span', 'jc-order-status__item-name', item.naam));
      li.append(el('span', 'jc-order-status__item-qty', `× ${item.aantal}`));
      ul.append(li);
    });
    return ul;
  };

  const shipmentBlock = (shipment, index, total) => {
    const block = el('li', 'jc-order-status__shipment');
    const title = shipment.vervoerder || fill(t.shipment, { n: index + 1 });
    block.append(el('h3', 'jc-order-status__shipment-title', total > 1 && shipment.vervoerder ? `${fill(t.shipment, { n: index + 1 })}: ${title}` : title));

    const dates = el('p', 'jc-order-status__meta');
    const parts = [];
    if (shipment.verzonden_op) parts.push(fill(t.shippedOn, { date: dateText(shipment.verzonden_op) }));
    if (shipment.bezorgd_op) parts.push(fill(t.deliveredOn, { date: dateText(shipment.bezorgd_op) }));
    dates.textContent = parts.join(' · ');
    if (parts.length) block.append(dates);

    if (shipment.trackingnummer) {
      const number = el('p', 'jc-order-status__tracking-number');
      number.append(el('span', 'jc-order-status__tracking-label', `${t.trackingNumber}: `));
      number.append(el('span', 'jc-order-status__tracking-value', shipment.trackingnummer));
      block.append(number);
    } else {
      block.append(el('p', 'jc-order-status__meta', t.noTracking));
    }

    const direct = shipment.trackinglink && httpsUrl(shipment.trackinglink);
    const base = shipment.trackinglink_postcode && httpsUrl(shipment.trackinglink_postcode);
    if (direct) block.append(externalLink(direct, t.track, 'btn btn--primary jc-order-status__track'));
    else if (base) block.append(postcodeForm(base));

    if (shipment.artikelen && shipment.artikelen.length && total > 1) block.append(items(shipment.artikelen));
    return block;
  };

  // Trustpilot (JAPCHRISTMAS-TRUSTPILOT-TEKSTEN.md ch. 3): the strings only hold
  // the sentence when the section setting is on. Same sentence for everyone.
  const reviewLine = () => {
    const href = httpsUrl(t.trustpilotUrl);
    const [before, rest = ''] = t.trustpilot.split('[link]');
    const [label, after = ''] = rest.split('[/link]');
    const line = el('p', 'jc-order-status__explanation jc-order-status__review', before);
    if (href && label) line.append(externalLink(href, label));
    line.append(after);
    return line;
  };

  const render = (order) => {
    const [label, explanation] = (t.status && t.status[order.status]) || [order.status, ''];
    const card = el('div', `jc-order-status__card jc-order-status__card--${order.status}`);

    const heading = el('h2', 'jc-order-status__order', fill(t.order, { name: order.bestelnummer }));
    heading.tabIndex = -1;
    card.append(heading);

    const meta = [];
    if (order.besteldatum) meta.push(fill(t.orderedOn, { date: dateText(order.besteldatum) }));
    meta.push(order.aantal_artikelen === 1 ? t.itemsOne : fill(t.itemsOther, { n: order.aantal_artikelen }));
    card.append(el('p', 'jc-order-status__meta', meta.join(' · ')));

    card.append(el('p', 'jc-order-status__label-status', label));
    if (explanation) card.append(el('p', 'jc-order-status__explanation', explanation));

    const shipments = order.zendingen || [];
    if (shipments.length) {
      const list = el('ul', 'jc-order-status__shipments');
      shipments.forEach((shipment, index) => list.append(shipmentBlock(shipment, index, shipments.length)));
      card.append(list);
    }
    if (order.status === 'verzonden' && t.trustpilot && t.trustpilotUrl) card.append(reviewLine());

    if (order.artikelen && order.artikelen.length) {
      card.append(el('h3', 'jc-order-status__items-title', t.items));
      card.append(items(order.artikelen));
    }

    result.replaceChildren(card);
    heading.focus({ preventScroll: true });
    heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = () => {
    const number = numberInput.value.trim();
    const email = emailInput.value.trim();
    const numberError = ORDER_NUMBER.test(number) ? '' : t.requiredNumber;
    const emailError = EMAIL.test(email) ? '' : t.requiredEmail;
    fieldError(numberInput, numberError);
    fieldError(emailInput, emailError);
    if (numberError) numberInput.focus();
    else if (emailError) emailInput.focus();
    return numberError || emailError ? null : { bestelnummer: number, email };
  };

  const errorText = (status, body) => {
    if (status === 404 || (body && body.fout === 'niet_gevonden')) return t.notFound;
    if (status === 429) return t.tooMany;
    if (status === 400) return t.notFound;
    return t.unavailable;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    showMessage('');
    const values = validate();
    if (!values) return;

    result.replaceChildren();
    setBusy(true);
    showStatusForScreenReaders(t.loading);
    try {
      const response = await fetch(form.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(values),
        credentials: 'same-origin',
        cache: 'no-store',
      });
      let body = null;
      try {
        body = await response.json();
      } catch (error) {
        body = null;
      }
      if (response.ok && body && body.gevonden && body.bestelling) {
        render(body.bestelling);
      } else {
        showMessage(errorText(response.status, body));
      }
    } catch (error) {
      showMessage(t.unavailable);
    } finally {
      setBusy(false);
      showStatusForScreenReaders('');
    }
  });

  [numberInput, emailInput].forEach((input) => {
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') === 'true') fieldError(input, '');
    });
  });

  function showStatusForScreenReaders(text) {
    // A polite line while the lookup runs; the result or the error replaces it.
    if (text) {
      result.replaceChildren(el('p', 'visually-hidden', text));
    } else if (result.firstElementChild && result.firstElementChild.classList.contains('visually-hidden')) {
      result.replaceChildren();
    }
  }
})();
